import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Col, Row, Button, Dropdown } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import ServerSideTable from '../../../../components/Table/ServerSideTable';
import { API_URL } from '../../../../utils/apiUrl';
import TruncatedTooltip from '../../../../components/TextExpand/TextExpand';
import BreadCrumb from '../../../../components/BreadCrumb/BreadCrumb';
import searchNormal from '@icons/search-icon.svg';
import '../Groups.scss';
import axiosWrapper from '../../../../utils/api';
import { Helmet } from 'react-helmet';
import plusIcon from '@icons/PlusWhite.svg';
import sideArrowIcon from '@icons/arrow-side.svg';
import excelIcon from '@icons/excelSign-icon.svg';
import AddParkerModal from '../Parker/AddParkerModal';
import { dateFormat } from '../../../../utils/common';
import * as XLSX from 'xlsx';
import BillingInvoiceGroup from './BillingInvoiceGroup';
import { pdf } from '@react-pdf/renderer';
import InvoiceDownloadRenderer from './InvoiceDownloadRenderer';
import PostPaymentModal from './PostPaymentModal';

const ViewGroupDetails = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [groupData, setGroupData] = useState(null);
    const [invoiceSearchTerm, setInvoiceSearchTerm] = useState('');
    const [debouncedInvoiceSearchTerm, setDebouncedInvoiceSearchTerm] = useState('');

    const { userToken } = useSelector((state) => state.auth);
    const { groupId } = useParams();
    const [showParkerModal, setShowParkerModal] = useState(false);
    const [tableKey, setTableKey] = useState(0);
    const invoiceTableRef = useRef(null);
    const parkerTableRef = useRef(null);
    const [showPostPaymentModal, setShowPostPaymentModal] = useState(false);

    // For edit mode
    const [editMode, setEditMode] = useState(false);
    const [selectedParkerId, setSelectedParkerId] = useState(null);
    const [parkersExcel, setParkersExcel] = useState([]);

    const [invoicesExcel, setInvoicesExcel] = useState([]);

    const [accountSummaryTotal, setAccountSummaryTotal] = useState(0);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 300);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedInvoiceSearchTerm(invoiceSearchTerm);
        }, 300);
        return () => clearTimeout(handler);
    }, [invoiceSearchTerm]);

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleInvoiceSearchChange = (e) => {
        setInvoiceSearchTerm(e.target.value);
    };

    const refreshParkersTable = () => {
        // Use the ref to refresh the grid via its exposed method
        if (parkerTableRef?.current) {
            parkerTableRef?.current?.refresh();
        }
    };

    const handleManageParker = (parkerId) => {
        setSelectedParkerId(parkerId);
        setEditMode(true);
        setShowParkerModal(true);
    };

    const handleAddParker = () => {
        setShowParkerModal(true);
    };

    const hideModal = () => {
        setShowParkerModal(false);
        setEditMode(false);
        setSelectedParkerId(null);
    };

    // Fetch Parker Excel data on first render (or when groupId changes)
    const fetchExcelData = async () => {
        // Pass the groupId as part of the URL (assuming your backend expects it as a path parameter)
        const response = await axiosWrapper(
            'get',
            `${API_URL.GET_ALL_PARKERS_OF_GROUP_ADMIN_EXCEL}/${groupId}`,
            {},
            userToken
        );
        if (response?.success) {
            // Map the data to a flattened structure
            const mappedData = response?.data?.map((parker) => {
                // Split the name field into firstName and lastName if possible
                const nameParts = parker?.name ? parker?.name.split(' ') : [];
                const firstName = nameParts.length > 0 ? nameParts[0] : '--';
                const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '--';
                // Join vehicle makes (if available)
                const vehicles =
                    parker?.vehicles && parker?.vehicles.length
                        ? parker?.vehicles.map((v) => v.vehicleMake || '--').join(', ')
                        : '--';
                return {
                    firstName,
                    lastName,
                    email: parker?.email || '--',
                    phoneNumber: parker?.phoneNumber || '--',
                    group: parker?.group || (parker?.groupAdmin && parker?.groupAdmin.groupName) || '--',
                    vehicles,
                    createdAt: parker?.createdAt ? dateFormat(parker?.createdAt) : ''
                };
            });
            setParkersExcel(mappedData || []);
        }
    };

    useEffect(() => {
        if (groupId) {
            fetchExcelData();
        }
    }, [groupId, userToken]);

    const parkersColDefs = useMemo(
        () => [
            {
                field: 'firstName',
                headerName: 'First Name',
                filter: 'agTextColumnFilter',
                cellRenderer: (params) => {
                    const text = params.value || '--';
                    return <TruncatedTooltip text={text} maxWidth={100} />;
                }
            },
            {
                field: 'lastName',
                headerName: 'Last Name',
                filter: 'agTextColumnFilter',
                cellRenderer: (params) => {
                    const text = params.value || '--';
                    return <TruncatedTooltip text={text} maxWidth={100} />;
                }
            },
            {
                field: 'email',
                headerName: 'Email',
                filter: 'agTextColumnFilter',
                cellRenderer: (params) => {
                    const text = params.value || '--';
                    return <TruncatedTooltip text={text} maxWidth={150} />;
                }
            },
            {
                field: 'group',
                headerName: 'Group',
                filter: 'agTextColumnFilter',
                cellRenderer: (params) => {
                    const text = params.value || '--';
                    return <TruncatedTooltip text={text} maxWidth={150} />;
                }
            },
            {
                // We'll pull the rate from subscription[0].amount if present
                field: 'rate',
                headerName: 'Rate',
                filter: 'agNumberColumnFilter',
                cellRenderer: (params) => {
                    // Find the subscription for this parker by matching parker ID
                    const parkerId = params.data?._id;
                    const sub = params.data?.subscription?.find((s) => s.metadata?.parkers?.includes(parkerId));
                    // If subscription amount is stored in cents, convert it to dollars.
                    const rateVal = sub ? sub.amount : 0;
                    return `$${Number(rateVal / 100).toFixed(2)}`;
                }
            },
            {
                // We'll pull the frequency from subscription[0].frequency if present
                field: 'frequency',
                headerName: 'Frequency',
                filter: 'agTextColumnFilter',
                cellRenderer: (params) => {
                    const parkerId = params.data?._id;
                    const sub = params.data?.subscription?.find((s) => s.metadata?.parkers?.includes(parkerId));
                    return sub ? sub.frequency : '--';
                }
            },
            {
                // We'll display the first two vehicle makes if more than one
                field: 'plates',
                headerName: 'Plates',
                filter: 'agTextColumnFilter',
                cellRenderer: (params) => {
                    const vehicles = params.data?.vehicles || [];
                    if (!vehicles.length) return '--';
                    if (vehicles.length === 1) {
                        return vehicles[0].licensePlate || '--';
                    }
                    // More than one, show first two
                    const firstTwo = vehicles.slice(0, 2).map((v) => v.licensePlate || '--');
                    return firstTwo.join(' | ');
                }
            },
            {
                // If there's a separate "balance" field in your data, you can reference it.
                // Otherwise, default to $0.00
                field: 'balance',
                headerName: 'Balance',
                filter: 'agNumberColumnFilter',
                maxWidth: '110',
                cellRenderer: (params) => {
                    const val = Number(params.value);
                    // If your API doesn't provide 'balance', you can parse from subscription or default to 0
                    if (isNaN(val)) return '$0.00';
                    return `$${val.toFixed(2)}`;
                }
            },
            // {
            //     field: 'notes',
            //     cellRenderer: (params) => <p>{params?.value || '--'}</p>
            // },
            {
                field: 'manage',
                headerName: '',
                filter: false,
                sortable: false,
                cellRenderer: (params) => {
                    return (
                        <Button
                            variant="success"
                            className="manage-btn"
                            onClick={() => handleManageParker(params?.data?._id)}
                            disabled={params?.data?.isInvite}
                        >
                            Manage <img className="ms-2" src={sideArrowIcon} alt="Side Arrow Icon" />
                        </Button>
                    );
                }
            }
        ],
        []
    );
    const accountSummaryColDefs = useMemo(
        () => [
            {
                field: 'invoiceNumber',
                headerName: 'Invoice',
                filter: 'agTextColumnFilter',
                cellRenderer: (params) => params.value || '--'
            },
            {
                field: 'invoiceTotal',
                headerName: 'Invoice Total',
                filter: 'agNumberColumnFilter',
                cellRenderer: (params) => {
                    const val = Number(params.value || 0);
                    return `$${val.toFixed(2)}`;
                }
            },
            {
                field: 'processingFee',
                headerName: 'Amount',
                filter: 'agNumberColumnFilter',
                cellRenderer: (params) => {
                    // If your data has a partial payment or something
                    const val = Number(params.value || 0);
                    return `$${val.toFixed(2)}`;
                }
            },
            {
                field: 'createdAt',
                headerName: 'Created Date',
                filter: 'agDateColumnFilter',
                cellRenderer: (params) => {
                    // If your backend returns a date or you have to parse it
                    return params.value ? dateFormat(params.value) : '--';
                }
            },
            {
                field: 'dueDate',
                headerName: 'Due Date',
                filter: 'agDateColumnFilter',
                cellRenderer: (params) => {
                    return params.value ? dateFormat(params.value) : '--';
                }
            },
            {
                field: 'status',
                headerName: 'Status',
                cellRenderer: (params) => {
                    const text = params.value || 'Pending';
                    const color = text === 'Due' || text === 'OVERDUE' ? 'red' : 'green';
                    return <span style={{ color }}>{text}</span>;
                }
            },
            {
                field: 'download',
                headerName: '',
                filter: false,
                sortable: false,
                cellRenderer: InvoiceDownloadRenderer
            }
        ],
        []
    );

    // Export using data already stored in state
    const exportToExcel = () => {
        if (parkersExcel.length > 0) {
            const worksheet = XLSX.utils.json_to_sheet(parkersExcel);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'GroupAdmins');
            XLSX.writeFile(workbook, 'GroupAdminsParker.xlsx');
        }
    };

    // Invoice //////////////////////////////////////////////////////////////////

    const fetchInvoicesExcelData = async () => {
        const response = await axiosWrapper(
            'get',
            `${API_URL.GET_ALL_GROUP_ADMIN_INVOICES_EXCEL}/${groupId}`,
            {},
            userToken
        );
        if (response?.success) {
            // Map data as needed; for example:
            const mappedInvoices = response?.data?.map((inv) => ({
                invoiceNumber: inv?.invoiceNumber,
                invoiceTotal: inv?.invoiceTotal,
                processingFee: inv?.processingFee,
                createdAt: inv?.createdAt ? dateFormat(inv?.createdAt) : '',
                dueDate: inv?.dueDate ? dateFormat(inv?.dueDate) : '',
                status: inv?.status
            }));
            setInvoicesExcel(mappedInvoices);
        }
    };

    const getGroupAdminById = async () => {
        try {
            const response = await axiosWrapper('get', API_URL.GET_GROUP_ADMIN_BY_ID(groupId), {}, userToken);
            if (response?.data) {
                setGroupData(response.data);
            }
        } catch (error) {
            setGroupData(null);
        }
    };

    useEffect(() => {
        if (groupId) {
            getGroupAdminById();
        }
    }, [groupId, userToken]);

    // Function to refresh invoices after paymen
    const refreshInvoices = () => {
        // You can either refresh the table via its ref or re-fetch invoice data
        if (invoiceTableRef?.current) {
            invoiceTableRef?.current?.refresh();
        }
        // Also, re-fetch invoices Excel data if needed
        fetchInvoicesExcelData();
    };

    useEffect(() => {
        if (groupId) {
            fetchInvoicesExcelData();
        }
    }, [groupId, userToken]);
    // B. Export invoice data to Excel
    const exportInvoicesToExcel = () => {
        if (invoicesExcel.length > 0) {
            const worksheet = XLSX.utils.json_to_sheet(invoicesExcel);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Invoices');
            XLSX.writeFile(workbook, 'GroupAdminInvoices.xlsx');
        }
    };

    // Once we've fetched the invoicesExcel data, we can compute total due
    useEffect(() => {
        if (invoicesExcel.length === 0) {
            setAccountSummaryTotal(0);
            return;
        }

        // Sum of all invoices marked as PAID
        const totalPaid = invoicesExcel
            .filter((inv) => inv.status === 'PAID')
            .reduce((acc, inv) => acc + (inv.invoiceTotal || 0), 0);

        // Sum of all invoices marked as OVERDUE
        const totalOverdue = invoicesExcel
            .filter((inv) => inv.status === 'OVERDUE' || inv.status === 'DUE' || inv.status === 'PENDING')
            .reduce((acc, inv) => acc + (inv.invoiceTotal || 0), 0);

        // Compute Total Due
        const totalDue = totalOverdue - totalPaid;

        setAccountSummaryTotal(totalDue);
    }, [invoicesExcel]);

    return (
        <>
            <Helmet>
                <title>Group Details | Level Parking</title>
            </Helmet>
            <BreadCrumb
                items={[
                    { label: 'Group', href: '/admin/groups' },
                    { label: 'Group Details', href: `/admin/group-details/${groupId}` },
                    { label: groupData?.groupName || '' }
                ]}
            />
            <div className="group-details-container">
                <Row className="align-items-center mb-3">
                    <Col>
                        <h4>Parkers</h4>
                    </Col>
                    <Col xs={12} sm={12} md={12} lg={12} xl={4} className="d-flex justify-content-end mt-3 mt-xl-0">
                        <div className="right-items">
                            <div className="search-input-container">
                                <input
                                    type="text"
                                    placeholder="Search"
                                    value={searchTerm}
                                    onChange={handleSearchChange}
                                />
                                <img src={searchNormal} alt="Search icon" className="search-icon" />
                            </div>
                            <div className="export-btn" onClick={exportToExcel}>
                                <img className="me-2 mb-1" src={excelIcon} alt="plus icon" />
                                Export
                            </div>

                            <Button className="add-btn" onClick={handleAddParker}>
                                <img className="me-2 mb-1" src={plusIcon} alt="plus icon" /> Add Parker
                            </Button>
                        </div>
                    </Col>
                </Row>

                <ServerSideTable
                    columns={parkersColDefs}
                    apiUrl={`${API_URL.GET_ALL_PARKERS_OF_GROUP_ADMIN}/${groupId}?`}
                    searchTerm={debouncedSearchTerm}
                    ref={parkerTableRef}
                />

                <hr className="my-5" />

                <Row className="align-items-center mb-3">
                    <Col>
                        <h4>
                            Account Summary -{' '}
                            <span style={{ color: accountSummaryTotal > 0 ? 'red' : 'green' }}>
                                ${accountSummaryTotal.toFixed(2) || 0.0}
                            </span>
                        </h4>
                    </Col>
                    <Col xs={12} sm={12} md={12} lg={12} xl={4} className="d-flex justify-content-end mt-3 mt-xl-0">
                        <div className="right-items">
                            <div className="search-input-container">
                                <input
                                    type="text"
                                    placeholder="Search"
                                    value={invoiceSearchTerm}
                                    onChange={handleInvoiceSearchChange}
                                />
                                <img src={searchNormal} alt="Search icon" className="search-icon" />
                            </div>
                            <div className="export-btn" onClick={exportInvoicesToExcel}>
                                <img className="me-2 mb-1" src={excelIcon} alt="plus icon" />
                                Export
                            </div>
                            {accountSummaryTotal > 0 && (
                                <Button className="add-btn" onClick={() => setShowPostPaymentModal(true)}>
                                    Post Payment
                                </Button>
                            )}
                        </div>
                    </Col>
                </Row>

                <ServerSideTable
                    columns={accountSummaryColDefs}
                    apiUrl={`${API_URL.GET_ALL_GROUP_ADMIN_INVOICES}/${groupId}?`}
                    searchTerm={debouncedInvoiceSearchTerm}
                    ref={invoiceTableRef}
                />
                {showParkerModal && (
                    <AddParkerModal
                        refreshParkers={refreshParkersTable}
                        show={showParkerModal}
                        onHide={hideModal}
                        groupId={groupId}
                        editMode={editMode}
                        parkerId={selectedParkerId}
                    />
                )}
            </div>
            <PostPaymentModal
                show={showPostPaymentModal}
                onHide={() => setShowPostPaymentModal(false)}
                totalDue={accountSummaryTotal}
                groupId={groupId}
                onPaymentSuccess={refreshInvoices}
            />
        </>
    );
};

export default ViewGroupDetails;
